import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './Signup.module.css';
import Button from '@/components/UI/Button';
import InputField from '@/components/UI/InputField';
import { Link, useNavigate } from 'react-router-dom';
import { AccountResourceService, ManagedUserVM } from '@/openapi';
import { useRemotePost } from '@/hooks/useRemotePost';
import Logo from '@/components/UI/Logo';
import Loader from '@/components/UI/Loader';
import SuccessToast from '@/components/UI/SuccessToast';
import FadeInSection from '@/components/UI/FadeInSection';
import { useViewportHeight } from '@/hooks/useViewportHeight';

const Signup = () => {
	const { t } = useTranslation();
	useViewportHeight();
	const navigate = useNavigate();

	const [email, setEmail] = useState('');
	const [username, setUsername] = useState('');
	const [password, setPassword] = useState('');
	const [privacyConfirmation, setPrivacyConfirmation] = useState(false);

	const [registerDone, setRegisterDone] = useState(false);
	const [registerError, setRegisterError] = useState(false);

	const [toastVisible, setToastVisible] = useState(false);
	const [toastMessage, setToastMessage] = useState('');
	const [toastType, setToastType] = useState<'success' | 'error'>('error');

	const emailInputRef = useRef<HTMLInputElement>(null);
	const usernameInputRef = useRef<HTMLInputElement>(null);
	const passwordInputRef = useRef<HTMLInputElement>(null);

	const register = useRemotePost<void, { requestBody: ManagedUserVM }>({
		remoteMethod: async (payload) => {
			return AccountResourceService.registerAccount(payload);
		}
	});

	useEffect(() => {
		if (typeof register.data !== 'undefined') {
			setRegisterDone(true);
			setToastMessage(t('signup.checkEmail'));
			setToastType('success');
			setToastVisible(true);
		}
	}, [register.data]);

	useEffect(() => {
		if (typeof register.error !== 'undefined') {
			let message = '';
			if (register.errorDetail === 'error.userexists' || register.errorDetail === 'Login name already used!') {
				message = t('signup.errUserAlreadyUsed');
			} else if (register.errorDetail === 'error.emailexists' || register.errorDetail === 'Email is already in use!') {
				message = t('signup.errMailAlreadyUsed');
			} else {
				message = t('signup.errGeneric');
			}
			setToastMessage(message);
			setToastType('error');
			setToastVisible(true);
			setRegisterDone(false);
			setRegisterError(true);
		}
	}, [register.error]);

	const submitHandler = async (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		setRegisterError(false);
		setRegisterDone(false);
		register.resetError();

		if (!email.trim() || !username.trim() || !password.trim()) {
			setToastMessage(t('signup.errMissingFields'));
			setToastType('error');
			setToastVisible(true);
			return;
		}

		if (!email.includes('@') || email.trim().length === 0 || email.length < 5 || email.length > 254) {
			setToastMessage(t('signup.errEmailLength'));
			setToastType('error');
			setToastVisible(true);
			return;
		}

		if (username.trim().length === 0 || username.length < 4 || username.length > 50) {
			setToastMessage(t('signup.errUsernameLength'));
			setToastType('error');
			setToastVisible(true);
			return;
		}

		if (password.trim().length === 0 || password.length < 4 || password.length > 50) {
			setToastMessage(t('signup.errPwdLength'));
			setToastType('error');
			setToastVisible(true);
			return;
		}

		const registerObject: ManagedUserVM = {
			login: username,
			email: email,
			langKey: 'it',
			createdBy: 'WiBro',
			password: password
		};

		setRegisterDone(true);
		await register.fetch({ requestBody: registerObject });
	};

	return (
		<div className={styles.main_container}>
			<form onSubmit={submitHandler} className={styles.form_container}>
				<Loader visible={register.loading} />

				<SuccessToast
					isVisible={toastVisible}
					message={toastMessage}
					type={toastType}
					onClose={() => setToastVisible(false)}
				/>

				<FadeInSection className={styles.form_box}>
					<div className={styles.form_header}>
						<Logo className={styles.logo} onClick={() => navigate('/home')} />
						<p className={styles.invitation_message}>{t('signup.invitation')}</p>
					</div>

					<div className={styles.form_content}>
						<div className={styles.credentials_div}>
							<div className={styles.credential_couple}>
								<InputField
									type={'email'}
									title={t('signup.email')}
									required
									label={t('signup.email')}
									placeholder={t('signup.emailPh')}
									labelClassName={styles.inputLabel}
									ref={emailInputRef}
									onChange={(e) => {
										setEmail(e.target.value);
									}}
									error={registerError}
								/>
							</div>
							<div className={styles.credential_couple}>
								<InputField
									type={'text'}
									title={t('signup.username')}
									required
									label={t('signup.username')}
									placeholder={t('signup.usernamePh')}
									labelClassName={styles.inputLabel}
									ref={usernameInputRef}
									onChange={(e) => {
										setUsername(e.target.value);
									}}
									error={registerError}
								/>
							</div>
							<div className={styles.credential_couple}>
								<InputField
									type={'password'}
									title={t('signup.password')}
									required
									label={t('signup.password')}
									placeholder={t('signup.passwordPh')}
									labelClassName={styles.inputLabel}
									ref={passwordInputRef}
									onChange={(e) => {
										setPassword(e.target.value);
									}}
									error={registerError}
								/>
							</div>
							<div className={styles.privacy_container}>
								<input
									className={styles.privacy_checkbox}
									type="checkbox"
									id="privacyConfirmation"
									checked={privacyConfirmation}
									onChange={(e) => setPrivacyConfirmation(e.target.checked)}
								/>
								<label htmlFor="privacyConfirmation">
									<Link className={styles.privacyLabel} to="/wibro-privacy-policy" target="_blank" rel="noopener noreferrer">
										{t('signup.confirmPrivacy')}
									</Link>
								</label>
							</div>
						</div>

						<div className={styles.box_btn}>
							<Button
								className={styles.btn_login}
								typeSubmit={'submit'}
								onClick={() => {}}
								name={t('signup.signIn')}
								disabled={!privacyConfirmation || register.loading || registerDone}
								color="black"
								background="white"
							/>
						</div>

						<Link to="/login" className={styles.navLink}>
							<p className={styles.forgotPass_label}>{t('signup.loginLink')}</p>
						</Link>
					</div>
				</FadeInSection>
			</form>
		</div>
	);
};

export default Signup;
